import { readContract, writeContract } from "@wagmi/core";
import { BSC_CHAIN_ID, wagmiConfig } from "./wagmi";

const ERC20_ABI = [
  {
    type: "function",
    name: "allowance",
    stateMutability: "view",
    inputs: [
      { name: "owner", type: "address" },
      { name: "spender", type: "address" },
    ],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    type: "function",
    name: "approve",
    stateMutability: "nonpayable",
    inputs: [
      { name: "spender", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [{ name: "", type: "bool" }],
  },
];

const CHECKOUT_ABI = [
  {
    type: "function",
    name: "buyCartWithQuote",
    stateMutability: "nonpayable",
    inputs: [
      {
        name: "items",
        type: "tuple[]",
        components: [
          { name: "productId", type: "uint256" },
          { name: "quantity", type: "uint256" },
        ],
      },
      {
        name: "q",
        type: "tuple",
        components: [
          { name: "buyer", type: "address" },
          { name: "totalUsdPrice", type: "uint256" },
          { name: "tokenPriceUsd", type: "uint256" },
          { name: "tokenAmount", type: "uint256" },
          { name: "deadline", type: "uint256" },
          { name: "quoteId", type: "bytes32" },
        ],
      },
      { name: "maxTokenAmount", type: "uint256" },
      { name: "signature", type: "bytes" },
    ],
    outputs: [],
  },
];

const getBigInt = () => {
  const bigIntFn =
    typeof window !== "undefined"
      ? window.BigInt
      : typeof global !== "undefined"
        ? global.BigInt
        : undefined;
  if (typeof bigIntFn !== "function") {
    throw new Error("BigInt is not supported in this environment");
  }
  return bigIntFn;
};

export const toBigInt = (value, label) => {
  if (typeof value === "bigint") return value;
  const bigIntFn = getBigInt();
  if (typeof value === "number") return bigIntFn(value);
  if (typeof value === "string" && value.trim() !== "") return bigIntFn(value);
  throw new Error(`Invalid ${label || "value"}`);
};

const formatError = (error) => {
  if (!error) return "Transaction failed";
  return error.shortMessage || error.message || "Transaction failed";
};

export const checkTokenAllowance = async ({ tokenAddress, owner, spender, requiredAmount }) => {
  try {
    if (!tokenAddress) throw new Error("Token address is required");
    if (!owner) throw new Error("Owner address is required");
    if (!spender) throw new Error("Spender address is required");

    const allowance = await readContract(wagmiConfig, {
      address: tokenAddress,
      abi: ERC20_ABI,
      functionName: "allowance",
      args: [owner, spender],
      chainId: BSC_CHAIN_ID,
    });

    const allowanceValue =
      typeof allowance === "bigint" ? allowance : getBigInt()(allowance);
    const result = {
      allowance: allowanceValue.toString(),
    };

    if (requiredAmount !== undefined && requiredAmount !== null) {
      const required = toBigInt(requiredAmount, "requiredAmount");
      result.isEnough = allowanceValue >= required;
    }

    return { success: true, data: result };
  } catch (error) {
    console.error("checkTokenAllowance error:", error);
    return { success: false, error: formatError(error) };
  }
};

export const approveToken = async ({ tokenAddress, spender, amount }) => {
  try {
    if (!tokenAddress) throw new Error("Token address is required");
    if (!spender) throw new Error("Spender address is required");

    const value = toBigInt(amount, "amount");

    const txId = await writeContract(wagmiConfig, {
      address: tokenAddress,
      abi: ERC20_ABI,
      functionName: "approve",
      args: [spender, value],
      chainId: BSC_CHAIN_ID,
    });

    return { success: true, data: { txId } };
  } catch (error) {
    console.error("approveToken error:", error);
    return { success: false, error: formatError(error) };
  }
};

export const buyCartWithQuote = async ({
  items,
  quote,
  maxTokenAmount,
  signature,
}) => {
    try {
    const contractAddress = process.env.REACT_APP_CHECKOUT_CONTRACT_ADDRESS;
    if (!Array.isArray(items) || items.length === 0) throw new Error("Items are required");
    if (!quote) throw new Error("Quote is required");
    if (!signature) throw new Error("Signature is required");

    const preparedItems = items.map((item, index) => {
      if (!item) throw new Error(`Item at index ${index} is invalid`);
      return {
        productId: toBigInt(item.productId, "productId"),
        quantity: toBigInt(item.quantity, "quantity"),
      };
    });

    const preparedQuote = {
      buyer: quote.buyer,
      totalUsdPrice: toBigInt(quote.totalUsdPrice, "totalUsdPrice"),
      tokenPriceUsd: toBigInt(quote.tokenPriceUsd, "tokenPriceUsd"),
      tokenAmount: toBigInt(quote.tokenAmount, "tokenAmount"),
      deadline: toBigInt(quote.deadline, "deadline"),
      quoteId: quote.quoteId,
    };

    const maxAmount =
      maxTokenAmount !== undefined && maxTokenAmount !== null
        ? toBigInt(maxTokenAmount, "maxTokenAmount")
        : preparedQuote.tokenAmount;

    const txId = await writeContract(wagmiConfig, {
      address: contractAddress,
      abi: CHECKOUT_ABI,
      functionName: "buyCartWithQuote",
      args: [preparedItems, preparedQuote, maxAmount, signature],
      chainId: BSC_CHAIN_ID,
    });

    return { success: true, data: { txId } };
  } catch (error) {
    console.error("buyCartWithQuote error:", error);
    return { success: false, error: formatError(error) };
  }
};

export const __abis = {
  ERC20_ABI,
  CHECKOUT_ABI,
};
